const clientGalleryBase = `${process.env.REACT_APP_BACKEND_URL}/api/static/clients/gallery`;
const galleryVersion = "clients-20260725-1";
const statusLabels = [
  "Completed",
  "Documentation Completed",
  "Documentation Complete",
  "Construction Started",
  "Consultation",
  "Contract Signed",
  "Priest Kitchen",
].sort((a, b) => b.length - a.length);

const toImageUrl = (fileName) => `${clientGalleryBase}/${fileName}?v=${galleryVersion}`;

export const parseClientLabel = (clientName) => {
  const normalizedName = clientName.replace(/\s+/g, " ").trim();
  const matchedStatus = statusLabels.find((status) => normalizedName.toLowerCase().endsWith(status.toLowerCase()));

  if (!matchedStatus) {
    return { displayName: normalizedName, status: "Completed" };
  }

  const statusStartIndex = normalizedName.toLowerCase().lastIndexOf(matchedStatus.toLowerCase());
  const baseName = normalizedName.slice(0, statusStartIndex).replace(/[—–-]\s*$/, "").trim();
  return { displayName: baseName, status: matchedStatus };
};

export const clientSections = [
  {
    slug: "hospitality-foodservice",
    title: "Hospitality & Food Service",
    description: "Restaurant, hotel, club, and hospitality food service consulting projects across the USA, Bahrain, India, and the UAE.",
    clients: [
      "Fish Taco — Tyson Corner, VA",
      "Don Pollo — Maryland",
      "La Brasita — Maryland",
      "Piccoli Piatti Pizzeria — Maryland",
      "La Gulla — Maryland",
      "Bombay Tandoor — Tyson Corner, VA",
      "Brazilia Café, Broadway, New York, NY",
      "Swissotel Chicago, IL",
      "The Stanford Court (Renaissance), San Francisco, CA",
      "Marriott at Metro Center, Washington, DC",
      "Marriott Courtyard — 8 properties in USA",
      "The Royal Golf Club at Riffa Views, Kingdom of Bahrain",
      "Aamby Valley, Lonavala, India",
      "Hotel Seaprincess, Bombay, India",
      "The Retreat, Bombay, India",
      "Sun-N-Sand Hotel, Bombay, India",
      "The Oberoi (East India Hotels), Bombay, India",
      "Deira Park Hotel, Dubai, United Arab Emirates",
      "Blue Diamond Hotel, Pune, India",
      "The Residency, Hyderabad, India",
      "Jewel of India, Bar & Restaurant, Bombay, India",
      "Ling's Pavilion, Bar & Restaurant, Bombay, India",
      "Sheetal, Bar & Restaurant, Bombay, India",
      "Celebration, Bar & Restaurant, Bombay, India",
      "The Avenue, Bar & Restaurant, Bombay, India",
    ],
  },
  {
    slug: "institutional-corporate",
    title: "Institutional, Education & Corporate",
    description: "Institutional dining, education, healthcare, government, and corporate facility consulting assignments supported through planning and design expertise.",
    clients: [
      "Washington & Lee University, Lexington, VA",
      "Federal Bureau of Prisons, Otisville, NY",
      "VAMC Medical Center, Cleveland, OH",
      "Mount Union College, Alliance, OH",
      "Yorktown High School, Arlington, VA",
      "Oklahoma State University – Student Union, Stillwater, OK",
      "Riffa View School, Kingdom of Bahrain",
      "IDBI Ltd, New Bombay, Bombay, India",
      "VSNL Ltd, Bombay, India",
      "Citibank HQ, Bombay, India",
      "Hindustan Lever Ltd, HQ, Bombay, India",
      "Reserve Bank of India, Bombay, India",
      "Pfizer Ltd, Bombay, Thane, Kalyani, Bombay, India",
      "Anjuman Institute of Hotel Management & Catering Technology, Bombay, India",
    ],
  },
  {
    slug: "religious-establishments",
    title: "Religious Establishments",
    description: "Temple, trust, and spiritual community projects supported through consulting, documentation, kitchen planning, completed work, and active development phases.",
    clients: [
      "Shri Saibaba Sansthan Trust, Shirdi, Ahmednagar, India",
      "Crescent Islamic Center, VA",
      "Jain Center of New Jersey, Somerset, NJ — Completed",
      "Sri Guruvaayoorappan Temple, Morganville, NJ — Completed",
      "Sri Venkateswara Lotus Temple, Fairfax, VA — Completed",
      "Shree Jalaram Seva Samaj NJ, Somerset, NJ — Completed",
      "Sringeri Vidya Bharati Foundation, Farmington Hills, MI — Completed",
      "Radha Madhav Society, Duluth, GA — Completed",
      "Nutan Haridham, Yogi Divine Society, Lake Hiawatha, NJ — Completed",
      "VRAJ, Schuylkill Haven, PA — Completed",
      "Gokuldham, Norcross, GA — Completed",
      "Radha Krishna Temple, Dallas, Texas — Completed",
      "Hindu Temple of Virginia — Documentation Completed",
      "Om Shri Balaji & Sai Temple, NJ — Completed",
      "Prem Mandir, Vrindhavan, India — Completed",
      "ISKCON, NJ — Documentation Completed",
      "Manhattan Sikh Center, NY — Completed",
      "Samarpan Hindu Temple, PA — Completed",
      "Bharatiya Hindu Temple, PA — Completed",
      "Hindu Temple of Virginia — Construction Started",
      "Guru Nanak Foundation of America, MD — Construction Started",
      "Radha Krishna Temple Ext, Dallas, Texas — Completed",
      "Srivari Sri Balaji Temple, NJ — Completed",
      "Eternal Mother Temple, MI — Completed",
      "JKP-HHE, Mangarh, India — Completed",
      "Iskcon of Washington DC — Completed",
      "Shiv Temple of Atlanta — Completed",
      "Radha Madhav Society, Toronto — Completed",
      "DCMSS - DC Metro Sai Samsthan — Documentation Complete",
      "Krishna Life, Atlanta — Documentation Complete",
      "Sri Bhakta Anjaneya Temple, MD — Contract Signed",
      "Sri Vidya Temple, NY — Documentation Complete",
      "Hindu Temple of Minnesota — Consultation",
      "Shiva Vishnu Temple, Maryland — Priest Kitchen",
    ],
  },
];

const inferRegion = (clientName) => {
  const value = clientName.toLowerCase();

  if (value.includes("bahrain")) return "Bahrain";
  if (value.includes("united arab emirates") || value.includes("dubai")) return "UAE";
  if (value.includes("india") || value.includes("bombay") || value.includes("pune") || value.includes("hyderabad") || value.includes("vrindhavan") || value.includes("mangarh") || value.includes("shirdi")) return "India";
  if (value.includes("toronto")) return "Canada";
  return "USA";
};

export const clientEntries = clientSections.flatMap((section) =>
  section.clients.map((clientName, index) => {
    const parsed = parseClientLabel(clientName);
    return {
      id: `${section.slug}-${index}`,
      name: parsed.displayName,
      rawName: clientName,
      status: parsed.status,
      category: section.title,
      categorySlug: section.slug,
      region: inferRegion(clientName),
    };
  }),
);

export const clientCategories = ["All", ...clientSections.map((section) => section.title)];
export const clientRegions = ["All", ...Array.from(new Set(clientEntries.map((entry) => entry.region)))];

export const clientGalleryItems = [
  { id: "gallery-1", title: "Hospitality Planning", image: toImageUrl("web-1-rest-bar.jpg") },
  { id: "gallery-2", title: "Banquet Space Layout", image: toImageUrl("web-banq-1.jpg") },
  { id: "gallery-3", title: "Banquet Detailing", image: toImageUrl("web-banq-2.jpg") },
  { id: "gallery-4", title: "Bar Experience", image: toImageUrl("web-bar-1.jpg") },
  { id: "gallery-5", title: "Kitchen Planning", image: toImageUrl("web-kit-1.jpg") },
  { id: "gallery-6", title: "Kitchen Coordination", image: toImageUrl("web-kit-2.jpg") },
  { id: "gallery-7", title: "OSU Kitchen Zone", image: toImageUrl("web-kit-osu.jpg") },
  { id: "gallery-8", title: "Service Flow", image: toImageUrl("web-serv-osu.jpg") },
  { id: "gallery-9", title: "Service Planning", image: toImageUrl("web-serv-osu-2.jpg") },
  { id: "gallery-12", title: "Brazilia Café Work", image: toImageUrl("brazilia-cafe-004.jpg") },
  { id: "gallery-13", title: "Brazilia Café Detail", image: toImageUrl("brazilia-cafe-025.jpg") },
  { id: "gallery-14", title: "Club Project", image: toImageUrl("brettonwood-cc-1.jpg") },
  { id: "gallery-15", title: "Club Dining", image: toImageUrl("brettonwood-cc-2.jpg") },
  { id: "gallery-16", title: "Temple Project", image: toImageUrl("jain-temple-002.jpg") },
  { id: "gallery-17", title: "Temple Interior", image: toImageUrl("jain-temple-010.jpg") },
  { id: "gallery-18", title: "Site Development", image: toImageUrl("punch-sgt-004.jpg") },
  { id: "gallery-19", title: "On-Site Progress", image: toImageUrl("punch-sgt-017.jpg") },
  { id: "gallery-20", title: "Project Documentation", image: toImageUrl("img-7424.jpg") },
  { id: "gallery-21", title: "Interior View", image: toImageUrl("img-7426.jpg") },
  { id: "gallery-22", title: "Execution Detail", image: toImageUrl("img-7435.jpg") },
  { id: "gallery-23", title: "Facility Overview", image: toImageUrl("img-7474.jpg") },
  { id: "gallery-24", title: "Project Walkthrough", image: toImageUrl("img-7559.jpg") },
];
